import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { BackTop, useScrollTop, Breadcrumb } from "@/components/feature/UI";
import { ChannelLabel, ListRow, StandardCard } from "@/components/feature/NewsCard";
import { news } from "@/mocks/news";
import { getChannel } from "@/utils/channel";
import { formatNum, siteName, useLdJson, useSeo } from "@/hooks/useSeo";

export default function DetailPage() {
  useScrollTop();
  const { id } = useParams();
  const item = news.find((n) => n.id === id);
  const channel = item ? getChannel(item.category) : undefined;

  const related = useRelated(item?.id ?? "");
  const [copied, setCopied] = useState(false);

  useSeo({
    title: item ? `${item.title} - ${siteName}` : `内容未找到 - ${siteName}`,
    description: item ? item.summary : "抱歉，你访问的内容不存在或已被下架。",
    keywords: item ? `${item.tags.join(",")},${item.categoryName}` : undefined,
  });

  const ld: object[] = item
    ? [
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: window.location.origin + "/" },
            { "@type": "ListItem", position: 2, name: channel?.name ?? "资讯", item: window.location.origin + `/channels?category=${item.category}` },
            { "@type": "ListItem", position: 3, name: item.title },
          ],
        },
        {
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          headline: item.title,
          description: item.summary,
          image: item.cover,
          datePublished: item.date,
          dateModified: item.date,
          author: { "@type": "Organization", name: siteName },
          publisher: { "@type": "Organization", name: siteName },
          articleSection: item.categoryName,
          keywords: item.tags.join(","),
          wordCount: item.content.join("").length,
          articleBody: item.content.join("\n"),
        },
      ]
    : [];
  useLdJson(ld, "detail-ld");

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    }
  };

  if (!item) {
    return (
      <>
        <Header />
        <main className="mx-auto flex min-h-[60vh] max-w-[1200px] flex-col items-center justify-center px-4 py-20 text-center">
          <i className="ri-file-search-line text-5xl text-foreground-300"></i>
          <h1 className="font-display mt-4 text-2xl font-bold text-foreground-900">这条新瓜找不到了</h1>
          <p className="mt-2 text-sm text-foreground-500">内容可能已被删除或地址有误。</p>
          <Link to="/channels" className="mt-6 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600">
            返回资讯列表
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="mx-auto grid max-w-[1200px] grid-cols-1 gap-8 px-4 py-8 md:px-6 lg:grid-cols-3">
        <article className="lg:col-span-2">
          <Breadcrumb
            items={[
              { label: channel?.name ?? "资讯", to: `/channels?category=${item.category}` },
              { label: item.title.slice(0, 20) },
            ]}
          />
          <header className="mt-5">
            <ChannelLabel category={item.category} />
            <h1 className="font-display mt-3 text-2xl font-black leading-tight text-foreground-950 md:text-4xl">
              {item.title}
            </h1>
            <p className="mt-3 border-l-2 border-primary-500 pl-3 text-sm leading-6 text-foreground-600 md:text-base">
              {item.summary}
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary-200 text-secondary-900">
                  <i className="ri-user-3-line text-xs"></i>
                </span>
                文 / {item.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {item.date} 发布
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {formatNum(item.reads)} 阅读
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {item.time}阅读
              </span>
            </div>
          </header>

          <div className="mt-6">
            <img src={item.cover} alt={item.title} title={item.title} className="w-full rounded-lg object-cover" />
          </div>

          <div className="mt-7 max-w-none">
            {item.content.map((p, i) => (
              <p key={i} className="mb-5 text-[15px] leading-8 text-foreground-700 md:text-base md:leading-9">
                {p}
              </p>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <span className="mr-1 flex items-center gap-1 text-sm font-medium text-foreground-700">
              <i className="ri-price-tag-3-line text-primary-500"></i>
              相关标签
            </span>
            {item.tags.map((t) => (
              <Link
                key={t}
                to={`/channels?q=${encodeURIComponent(t)}`}
                className="rounded-full border border-background-300 bg-background-50 px-3 py-1 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                #{t}
              </Link>
            ))}
          </div>

          <div className="mt-6 flex flex-col gap-3 rounded-lg bg-background-100/70 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex gap-1.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 text-background-50 transition-colors hover:bg-primary-600">
                  <i className="ri-wechat-line text-sm"></i>
                </span>
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-500 text-foreground-950 transition-colors hover:bg-accent-600">
                  <i className="ri-weibo-line text-sm"></i>
                </span>
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-foreground-800 text-background-50 transition-colors hover:bg-foreground-950">
                  <i className="ri-link-m text-sm"></i>
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={copyLink}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className={copied ? "ri-check-line" : "ri-share-forward-line"}></i>
              {copied ? "链接已复制" : "复制分享链接"}
            </button>
          </div>

          <div className="mt-6 flex items-start gap-3 rounded-lg border border-accent-200 bg-accent-100/60 p-4">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-500 text-foreground-950">
              <i className="ri-lightbulb-flash-line text-sm"></i>
            </span>
            <div className="text-sm leading-6 text-foreground-700">
              <span className="font-semibold text-foreground-950">理性吃瓜提示：</span>
              本站内容以讨论行业现象与公共话题为主，相关人物与事件多采用化名处理。信息可能持续更新或存在不同说法，请读者理性看待，切勿对号入座或进行人身攻击。
            </div>
          </div>

          <div className="mt-8 rounded-lg border border-background-200 bg-background-50 p-5">
            <div className="flex items-center justify-between">
              <h4 className="font-display text-lg font-bold text-foreground-950">相关阅读</h4>
              <Link to="/channels" className="text-xs text-foreground-500 hover:text-primary-600">
                更多 →
              </Link>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {related.map((n) => (
                <StandardCard key={n.id} item={n} showSummary={false} />
              ))}
            </div>
          </div>
        </article>

        <aside className="flex flex-col gap-6">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h3 className="font-display flex items-center gap-2 text-base font-bold text-foreground-950">
              <span className="flex h-6 w-6 items-center justify-center rounded-md bg-accent-500 text-foreground-950">
                <i className="ri-flashlight-line text-xs"></i>
              </span>
              栏目其他速递
            </h3>
            <div className="mt-4 flex flex-col gap-4">
              {related.slice(0, 4).map((n, i) => (
                <ListRow key={n.id} item={n} index={i} />
              ))}
            </div>
          </div>

          <div className="relative overflow-hidden rounded-lg">
            <div className="relative h-48 w-full">
              <img
                src="https://readdy.ai/api/search-image?query=editorial%20photography%20of%20a%20notebook%20and%20pen%20under%20warm%20lamp%20with%20coffee%20on%20wooden%20desk%2C%20amber%20and%20deep%20red%20tones%2C%20moody%20journalistic%20vibe%2C%20high%20detail%20magazine%20style&width=800&height=600&seq=detail-sub-bg&orientation=squarish"
                alt="投稿与线索"
                title="向每日新瓜投稿"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-primary-900/85"></div>
            </div>
            <div className="absolute inset-0 flex flex-col items-start justify-center p-5">
              <span className="inline-flex items-center gap-1 rounded-md bg-background-50/15 px-2 py-1 text-xs text-background-50">
                <i className="ri-megaphone-line"></i>
                有线索？别藏着
              </span>
              <h4 className="font-display mt-3 text-lg font-bold leading-snug text-background-50">投稿爆料</h4>
              <p className="mt-1 text-xs leading-5 text-background-100/80">匿名提交你掌握的新瓜线索，编辑核实后谨慎发布。</p>
              <Link to="/contact" className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-accent-500 px-4 py-2 text-sm font-semibold text-foreground-950 transition-colors hover:bg-accent-600">
                去投稿
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </aside>
      </main>
      <Footer />
      <BackTop />
    </>
  );
}

function useRelated(selfId: string) {
  const current = news.find((n) => n.id === selfId);
  if (!current) return news.slice(0, 4);
  const same = news.filter((n) => n.id !== selfId && n.category === current.category);
  const others = news.filter((n) => n.id !== selfId && n.category !== current.category);
  const merged = [...same, ...others];
  return merged.slice(0, 4);
}